"use client";

import OrderDetails from "@/components/templates/Checkout/components/order-details";
import { Locales } from "@/lib/i18n";
import { __ } from "@/lib/i18n/functions";
import { cn, formatPrice } from "@/lib/utils";
import type { StoreOrder } from "@medusajs/types";
import { useSearchParams } from "next/navigation";

type ThankYouSummary = {
  readonly order: StoreOrder;
  readonly locale: Locales;
};

const ThankYouSummary = ({ order, locale }: ThankYouSummary) => {
  const searchParams = useSearchParams();
  const workflowError = searchParams.get("error");

  return (
    <article
      className={cn(
        "border-primary-light/50 w-full space-y-4 max-md:pb-5 md:max-w-[571px] md:flex-2/3 md:space-y-5 md:rounded-md md:bg-white md:p-4",
        workflowError && "md:max-w-none"
      )}
    >
      <h3 className="text-1xl font-cabinet-grotesk font-medium">{__("Summary of your order", locale)}</h3>
      <OrderDetails order={order} locale={locale} />
      <dl className="border-primary-light/50 border-t pt-5">
        <div className="flex items-center justify-between">
          <dd className="text-primary-light">{__("Total VAT exc.", locale)}</dd>
          <dt>{formatPrice(order?.item_subtotal || 0)}</dt>
        </div>
        {!!order?.discount_total && (
          <div className="flex items-center justify-between">
            <dd className="text-primary-light">{__("Promo code", locale)}</dd>
            <dt> -{formatPrice(order?.discount_total || 0)}</dt>
          </div>
        )}
        <div className="flex items-center justify-between">
          <dd className="text-primary-light">{__("VAT (21%)", locale)}</dd>
          <dt>{formatPrice(order?.tax_total)}</dt>
        </div>
        <hr className="border-primary-light/50 my-5" />
        <div className="flex items-start justify-between">
          <dd className="leading-h1 text-lg font-semibold">
            {__("Total VAT", locale)}* <br />
            <span className="text-primary-light text-xs font-normal">
              *{__("Shipping costs not included", locale)} ...
            </span>
          </dd>
          <dt className="text-xl font-semibold">{formatPrice(order?.total || 0)}</dt>
        </div>
      </dl>
    </article>
  );
};

export default ThankYouSummary;
